import { AssignmentType } from "~/types/enums/AssignmentType.enum";
import { DocumentState } from "~/types/enums/DocumentState";
import { DocumentSubmissionType } from "~/types/enums/DocumentSubmissionType.enum";
import { SubmittedDocument } from "~/types/SubmittedDocument";

export const useCreateSubmittedDocumentFromParams = ({
  name,
  firestoreTimestamp,
  params,
}: {
  firestoreTimestamp: any;
  name: string;
  params?: {
    userId: string;
    profileId?: string;
    enableStudentThesaurus?: boolean;
    enableStudentReview?: boolean;
    classroom?: {
      id: string;
      name: string;
      enableStudentReview?: boolean;
      enableSubmissionBadges?: boolean;
      enableStudentSubmissionCreation?: boolean;
      studentSubmissionCreationStudentIds?: string[];
      enableStudentCompare?: boolean;
      studentCompareStudentIds?: string[];
      enableStudentThesaurus?: boolean;
      studentThesaurusStudentIds?: string[];
    };
    assignment?: {
      id: string;
      name: string;
      openTimestamp?: number;
      dueTimestamp?: number;
      closeTimestamp?: number;
      isVisibleToStudents?: boolean;
      allowKWOSubmission?: boolean;
      allowRoughDraftSubmission?: boolean;
      requiredKWOSubmission?: boolean;
      requireRoughDraftSubmission?: boolean;
      requireRoughDraftChecklist?: boolean;
      roughDraftSubmissionFormats?: string[];
      allowFinalDraftSubmission?: boolean;
      allowFinalDraftGradedState?: boolean;
      isAutoReturnEnabled?: boolean;
      type?: AssignmentType;
      levelId?: string;
      lessonId?: string;
    };
    student?: {
      id: string;
      name: string;
    };
    checklist?: {
      id: string;
      path: string;
    };
    allowFinalDraftGradedState?: boolean;
    documentSubmissionType?: DocumentSubmissionType;
    googleDriveFileId?: string;
    fileType?: string;
  };
}) => {
  // Ok so once a file is uploaded we want to first create a document
  const timestamp = Date.now();
  const submittedDocument: SubmittedDocument = {
    name: name,
    state: DocumentState.uploading,
    createOnTimestamp: timestamp,
    lastUpdatedTimestamp: timestamp,
    userId: params?.userId,
    isLate: false,
    submittedAtTimestamp: firestoreTimestamp,
    submittedAtTimestampMillis: timestamp,
    doesNOTExpectAssignment: false,
    isSample: false,
    documentSubmissionType: params?.documentSubmissionType,
    allowFinalDraftGradedState: params?.allowFinalDraftGradedState,
    googleDriveFileId: params?.googleDriveFileId,
    fileType: params?.fileType,
    enableStudentThesaurus: params?.enableStudentThesaurus,
    enableStudentReview: params?.enableStudentReview,
  } as SubmittedDocument;

  if (params?.profileId) {
    submittedDocument.profileId = params.profileId;
  }

  if (params?.classroom) {
    submittedDocument.classroomId = params.classroom.id;
    submittedDocument.classroomName = params.classroom.name;
    submittedDocument.enableStudentReview =
      params.classroom.enableStudentReview;
    submittedDocument.enableSubmissionBadges =
      params.classroom.enableSubmissionBadges;
    submittedDocument.enableStudentSubmissionCreation =
      params.classroom.enableStudentSubmissionCreation;
    submittedDocument.enableStudentCompare =
      params.classroom.enableStudentCompare;
    params.classroom.studentCompareStudentIds;
    submittedDocument.enableStudentThesaurus =
      params.classroom.enableStudentThesaurus;
  }

  if (params?.assignment) {
    submittedDocument.assignmentId = params.assignment.id;
    submittedDocument.assignmentName = params.assignment.name;
    submittedDocument.assignmentOpenTimestamp = params.assignment.openTimestamp;
    submittedDocument.assignmentDueTimestamp = params.assignment.dueTimestamp;
    submittedDocument.assignmentCloseTimestamp =
      params.assignment.closeTimestamp;
    submittedDocument.assignmentIsVisibleToStudents =
      params.assignment.isVisibleToStudents;
    submittedDocument.assignmentAllowKWOSubmission =
      params.assignment.allowKWOSubmission;
    submittedDocument.assignmentAllowRoughDraftSubmission =
      params.assignment.allowRoughDraftSubmission;
    submittedDocument.assignmentRequiredKWOSubmission =
      params.assignment.requiredKWOSubmission;
    submittedDocument.assignmentRequireRoughDraftSubmission =
      params.assignment.requireRoughDraftSubmission;
    submittedDocument.assignmentRequireRoughDraftChecklist =
      params.assignment.requireRoughDraftChecklist;
    submittedDocument.assignmentRoughDraftSubmissionFormats =
      params.assignment.roughDraftSubmissionFormats;
    submittedDocument.assignmentAllowFinalDraftSubmission =
      params.assignment.allowFinalDraftSubmission;
    submittedDocument.assignmentAllowFinalDraftGradedState =
      params.assignment.allowFinalDraftGradedState;
    submittedDocument.assignmentIsAutoReturnEnabled =
      params.assignment.isAutoReturnEnabled;
    submittedDocument.assignmentType = params.assignment.type;
    submittedDocument.assignmentLevelId = params.assignment.levelId;
    submittedDocument.assignmentLessonId = params.assignment.lessonId;
    submittedDocument.isLate =
      (params.assignment.dueTimestamp &&
        submittedDocument.submittedAtTimestampMillis &&
        submittedDocument.submittedAtTimestampMillis >
          params.assignment.dueTimestamp) == true;
  }

  if (params?.student) {
    submittedDocument.studentId = params.student.id;
    submittedDocument.studentName = params.student.name;
  }

  if (
    params?.checklist &&
    submittedDocument.documentSubmissionType !=
      DocumentSubmissionType.keyWordOutline
  ) {
    submittedDocument.checklistId = params.checklist.id;
    submittedDocument.checklistPath = params.checklist.path;
  }

  return submittedDocument as SubmittedDocument;
};
